import React from 'react';
import { CitationBadge } from '@/components/chat/citations/CitationBadge';

export interface CitationRenderOptions {
  messageId: string;
  onCitationClick: (number: number) => void;
  hoveredCitation: number | null;
  onCitationHover: (number: number | null) => void;
  /** Total count of each citation number across the full message. */
  totalCounts: Map<number, number>;
  /** Mutable counter ref — shared across all calls within one render of a message. */
  counterRef: React.MutableRefObject<Map<number, number>>;
}

export function renderTextWithCitations(
  text: string,
  options: CitationRenderOptions | ((number: number) => void)
): React.ReactNode[] {
  // When shorthand function is used, pre-compute total counts for this specific text
  const isShorthand = typeof options === 'function';
  const precomputedCounts = isShorthand ? (() => {
    const counts = new Map<number, number>();
    const regex = /\[(\d+)\]/g;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(text)) !== null) {
      const n = parseInt(m[1], 10);
      if (n > 0) counts.set(n, (counts.get(n) ?? 0) + 1);
    }
    return counts;
  })() : undefined;

  const opts: CitationRenderOptions = isShorthand
    ? {
        messageId: '',
        onCitationClick: options,
        hoveredCitation: null,
        onCitationHover: () => {},
        totalCounts: precomputedCounts!,
        counterRef: { current: new Map() },
      }
    : options;

  const citationRegex = /\[(\d+)\]/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = citationRegex.exec(text)) !== null) {
    const citationNumber = parseInt(m[1], 10);
    if (citationNumber <= 0) continue;

    if (m.index > lastIndex) {
      parts.push(text.slice(lastIndex, m.index));
    }

    const counter = opts.counterRef.current;
    const idx = counter.get(citationNumber) ?? 0;
    counter.set(citationNumber, idx + 1);

    const total = opts.totalCounts.get(citationNumber) ?? 1;
    const isLast = idx === total - 1;

    parts.push(
      <CitationBadge
        key={`citation-${opts.messageId}-${citationNumber}-${idx}`}
        number={citationNumber}
        messageId={opts.messageId}
        occurrenceIndex={idx}
        isLast={isLast}
        isHighlighted={opts.hoveredCitation === citationNumber}
        onClick={() => opts.onCitationClick(citationNumber)}
        onMouseEnter={() => opts.onCitationHover(citationNumber)}
        onMouseLeave={() => opts.onCitationHover(null)}
      />
    );

    lastIndex = m.index + m[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}
